/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// reactstrap components
import { Col, Form } from 'reactstrap';

import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import getDeviceData from '../api/DeviceInfo';
import getParamFromURL from '../api/getParamFromURL';
import { GlobalContext } from '../api/GlobalContext';
import { storeLocalData } from '../api/Common';
import { logGAEvent } from '../api/googleAnalytics';
import { toast } from 'react-toastify';
import GoogleSignInButton from '../components/Misc/GoogleSignInButton';
import MicrosoftSignInButton from '../components/Misc/MicrosoftSignInButton';
import {
  getDemoAnonymousId,
  clearDemoAnonymousId,
} from '../utils/demoTracking';
import {
  EMR_OPTIONS,
  PROFESSION_OPTIONS,
  SPECIALITY_OPTIONS,
} from '../constants/EmrNoteTypeMapping';

const Register = () => {
  // Get the API
  const { apiClient } = useContext(GlobalContext);
  const cs = getParamFromURL('cs');

  // Arriving from the Google OAuth callback. The token stands in for a password
  // and is bound to this exact address, so the email field is locked and the
  // password fields are hidden entirely. Everything else - Profession, EMR, and
  // crucially the BAA + Terms checkbox - still has to be filled in, which is
  // why Google users come through this form at all instead of being
  // auto-provisioned.
  const googleSignupToken = getParamFromURL('gst', '');
  const isGoogleSignup = !!googleSignupToken;
  // The Microsoft callback mints the same token and tags the URL with
  // idp=microsoft so the copy below names the right provider.
  const signupIdpName =
    getParamFromURL('idp', '') === 'microsoft' ? 'Microsoft' : 'Google';
  const embedOrigin = getParamFromURL('embedOrigin', '');

  useEffect(() => {
    if (cs) {
      apiClient.getCheckoutSession(cs).then((data) => {
        setPromoCode(data.referralCode);
        setName(data.name);
        setEmail(data.email);
        if (data.referralCode) setPromoLocked(true);
      });
    }
  }, []);

  // Get the navigation function
  const navigate = useNavigate();
  const location = useLocation();

  // Coming back from "Wrong address?" on the check-your-email screen: the
  // answers the user already gave ride along in router state (in memory, never
  // the URL) so they only have to fix the email. The password is deliberately
  // NOT carried and has to be re-entered. Empty object on a normal visit.
  const draft = location.state?.signupDraft || {};
  const emailInputRef = useRef(null);

  // // State variable to store the label text
  const [labelText, setLabelText] = useState('Sign up');
  const embedAuthPopup = useRef(null);

  useEffect(() => {
    const receiveEmbedAuth = (event) => {
      const popup = embedAuthPopup.current;
      const result = event.data;
      if (
        !popup ||
        event.origin !== window.location.origin ||
        event.source !== popup ||
        result?.type !== 'COMP_EMBED_AUTH_RESULT'
      ) return;

      const sessionKey = Number(result.sessionKey);
      if (!Number.isSafeInteger(sessionKey) || !result.sessionEmail) return;

      storeLocalData(String(sessionKey));
      storeLocalData(result.sessionEmail, true);
      apiClient.setSessionKey(sessionKey);
      apiClient.setSessionEmail(result.sessionEmail);
      popup.postMessage({ type: 'COMP_EMBED_AUTH_ACK' }, window.location.origin);
      embedAuthPopup.current = null;
      navigate('/User/Home');
    };
    window.addEventListener('message', receiveEmbedAuth);
    return () => window.removeEventListener('message', receiveEmbedAuth);
  }, [apiClient, navigate]);

  const startEmbedAuth = (flow) => {
    if (!flow) return;
    if (!flow.popup) {
      setLabelText('Please allow pop-ups to finish signing in.');
      return;
    }
    embedAuthPopup.current = flow.popup;
  };

  // State variables to store the form input values
  const [name, setName] = useState(draft.name ?? getParamFromURL('name', ''));
  const [email, setEmail] = useState(
    draft.email ?? getParamFromURL('email', ''),
  );
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isTypingConfirm, setIsTypingConfirm] = useState(false);
  const typingTimeoutRefConfirm = useRef(null);

  const [profession, setProfession] = useState(draft.profession ?? '');
  const [speciality, setSpeciality] = useState(draft.speciality ?? '');
  const [emr, setEmr] = useState(draft.emr ?? '');
  const referralParamFromURL = getParamFromURL('referralCode', '');
  const [promoCode, setPromoCode] = useState(
    draft.promoCode ?? referralParamFromURL,
  );
  const [promoLocked, setPromoLocked] = useState(!!referralParamFromURL);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [remember, setRemember] = useState(false);
  const [disableCreateAccount, setDisableCreateAccount] = useState(false);

  // Signup is a 2-step wizard: step 1 collects identity/credentials, step 2
  // collects practice details. Nothing here is submitted to the API until
  // step 2's "Create account" is pressed - step 1's "Continue" only validates
  // and advances.
  const [step, setStep] = useState(1);
  // These four must stay in lockstep with the backend password policy in
  // Auth.AuthNewUser (>=8 chars, a lowercase, an uppercase, and a number).
  // They drive both the live requirement chips and the step-1 gate, so a
  // password that turns every chip green is one the backend will accept.
  const hasMinLength = password.length >= 8;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const passwordMeetsPolicy =
    hasMinLength && hasLowercase && hasUppercase && hasNumber;

  const [deviceData, setDeviceData] = useState({});
  const [isTypingConfirmError, setIsTypingConfirmError] = useState(false);

  const typingTimeoutRefConfirmError = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getDeviceData();
      setDeviceData(data);
    };

    fetchData();
  }, []);

  // Returning from "Wrong address?" - the email is prefilled with the typo, so
  // drop the cursor straight into it (and select it) rather than making the
  // user hunt for the field they came back to fix.
  useEffect(() => {
    if (draft.email && emailInputRef.current) {
      emailInputRef.current.focus();
      emailInputRef.current.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to handle form submission
  function handleSubmit(e) {
    e.preventDefault();
    setDisableCreateAccount(true);

    const isPromoError = (msg) => {
      if (!msg || typeof msg !== 'string') return false;
      const text = msg.toLowerCase();
      return (
        text.includes('promo') ||
        text.includes('referral') ||
        text.includes('invalid code') ||
        text.includes('invalid referral') ||
        text.includes('seat') // backend returns "No seats available. Contact {reseller_name} directly..." for resellers in SELF_SERVE_SEAT_DISABLED
      );
    };

    //RegEx for email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Step 1 owns identity + credentials + the terms checkbox. "Continue"
    // and "Create account" both submit this same form, so route on which
    // step is showing rather than duplicating the form/handler.
    if (step === 1) {
      //check if email format is valid
      if (!emailRegex.test(email)) {
        setLabelText(
          'Invalid email format. Please provide a valid email address',
        );
        setDisableCreateAccount(false);
        return;
      }
      if (email.endsWith('@corahealth.com')) {
        if (window.location.href.includes('chrome-extension://')) {
          console.log('isCX');
          setLabelText(
            <span style={{ color: '#dc3545' }}>
              <i
                className="fa-solid fa-triangle-exclamation"
                style={{ marginRight: '8px' }}
              ></i>
              For a Cora Health employee, to access Comprehend, go to the CORA
              Connect Home. Under CORA Links, click Comprehend Health to
              automatically sign up.
            </span>,
          );
          setDisableCreateAccount(false);
          return;
        } else {
          window.location.href =
            'https://launcher.myapps.microsoft.com/api/signin/6d527a2b-32ef-4d98-8c7e-4be31fe643e5?tenantId=5d1837c8-8290-4cf9-83e1-c0b1ff56dc97';
        }
      }
      // Passwords must match. This used to only be checked when the terms box was
      // unticked, which meant a mismatched pair could actually be submitted.
      if (!isGoogleSignup && password !== confirmPassword) {
        setLabelText("Passwords don't match.");
        setPasswordError('Passwords do not match.');
        setDisableCreateAccount(false);
        return;
      }
      // Enforce the full password policy here, on step 1, so a weak password is
      // caught the moment they click Continue - not after they've filled in the
      // practice details on step 2 and hit the backend. The chips above show
      // exactly which rule is unmet.
      if (!isGoogleSignup && !passwordMeetsPolicy) {
        setLabelText(
          'Password must be at least 8 characters and include a lowercase letter, an uppercase letter, and a number.',
        );
        setDisableCreateAccount(false);
        return;
      }
      // Check that the user agreed to the terms
      if (!agreeTerms) {
        setLabelText('Please agree to the terms before signing up.');
        setDisableCreateAccount(false);
        return;
      }

      // Everything on this step checks out - move on to practice details.
      setLabelText('Sign up');
      setDisableCreateAccount(false);
      setStep(2);
      return;
    }

    // Step 2 owns practice details and is the one that actually creates the account.
    if (!profession) {
      setLabelText('Please fill Profession.');
      setDisableCreateAccount(false);
      return;
    }
    if (!emr) {
      setLabelText('Please select an EMR system.');
      setDisableCreateAccount(false);
      return;
    }

    // Tell the user we got their request
    setLabelText('Attempting to sign up...');

    // Call the login function with the form values
    apiClient
      .register(
        name,
        email,
        password,
        profession,
        speciality,
        emr,
        promoCode,
        remember,
        deviceData.IP,
        deviceData.osType,
        deviceData.osVersion,
        deviceData.browser,
        deviceData.userAgent,
        deviceData.appVersion,
        googleSignupToken,
        // Pre-signup self-serve demo trail, if this browser has one. Read
        // (never minted) here — signing up is not the moment to start tracking
        // someone who was never on the demo.
        getDemoAnonymousId(false),
      )
      .then((data) => {
        // If there is an error, display it in the Sign In Area
        if ('error' in data) {
          // Send the error to the small
          setLabelText(data['error']);
          // If the error is promo-related, clear and unlock the promo field
          if (isPromoError(data['error'])) {
            setPromoCode('');
            setPromoLocked(false);
          }
        } else {
          // The demo trail now belongs to this account. Drop the id so the next
          // person to sign up on a shared clinic machine doesn't inherit it.
          clearDemoAnonymousId();

          // Log the account creation event. Skipped when the backend tells us
          // this was a repeat signup for an existing unverified account - no
          // account was created, so counting it would inflate the funnel.
          if (!data?.resent) {
            logGAEvent('create_account', apiClient, {
              email: email,
              name: name,
              emr: emr,
              referralCode: data?.referralCode || '',
            });
          }

          if (data?.emailVerificationRequired || !data?.sessionKey) {
            // The account exists but is dormant until the user clicks the link
            // in the verification email, so there is no session to redirect
            // into. Park them on the "check your email" screen instead. Carry
            // the answers along (in router state, not the URL, and without the
            // password) so "Wrong address?" there can bring them back to this
            // form prefilled if the email was mistyped.
            navigate('/auth/verify/sent?email=' + encodeURIComponent(email), {
              state: {
                isStudent: data?.isStudent,
                signupDraft: {
                  name,
                  email,
                  profession,
                  speciality,
                  emr,
                  promoCode,
                },
              },
            });
          } else {
            // Finish an Embed signup in the same popup so the iframe receives
            // the session through the existing completion page.
            if (
              googleSignupToken &&
              window.opener &&
              [
                'https://embed.comprehendpt.com',
                'https://dev.embed.comprehendpt.com',
                'http://localhost:3008',
              ].includes(embedOrigin)
            ) {
              const fragment = new URLSearchParams({
                sessionKey: data.sessionKey,
                sessionEmail: email,
              });
              window.location.replace(
                `${embedOrigin}/embed-auth-complete.html#${fragment}`,
              );
            } else {
              if (data?.isStudent) toast.success("Thanks for being a student! Your account is free for 2 years.");
              navigate('/');
            }
          }
        }
        setDisableCreateAccount(false);
      })
      .catch((e) => {
        setLabelText('Unexpected JS Error. Contact Jake');
        setDisableCreateAccount(false);
      });
  }

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);

    // Error message: show after 0.8s so it doesn't flash on every keystroke.
    setIsTypingConfirmError(true);
    if (typingTimeoutRefConfirmError.current)
      clearTimeout(typingTimeoutRefConfirmError.current);
    typingTimeoutRefConfirmError.current = setTimeout(() => {
      setIsTypingConfirmError(false);
    }, 800);
  };

  // Step 1's fields are untouched when going back - just re-show them.
  const handleBack = () => {
    setLabelText('Sign up');
    setStep(1);
  };

  return (
    <>
      <Col lg="6" md="8">
        <div className="cptx-auth">
          <div className="cptx-card">
            <div className="cptx-card-body">
              <div className="cptx-progress">
                <i className="cptx-done" />
                <i className={step === 2 ? 'cptx-done' : ''} />
              </div>

              {step === 2 && (
                <button
                  type="button"
                  className="cptx-back-link"
                  onClick={handleBack}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 18l-6-6 6-6"
                      stroke="currentColor"
                      strokeWidth="2.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Back
                </button>
              )}

              {step === 1 && !isGoogleSignup && (
                <>
                  <GoogleSignInButton label="Sign up with Google" onStarted={startEmbedAuth} />
                  <div style={{ marginTop: '10px' }}>
                    <MicrosoftSignInButton label="Sign up with Microsoft" onStarted={startEmbedAuth} />
                  </div>
                  <div className="cptx-divider">
                    <span>or sign up with your email</span>
                  </div>
                </>
              )}

              {labelText && labelText !== 'Sign up' && (
                <div className="cptx-status">
                  <small>{labelText}</small>
                </div>
              )}

              <Form role="form" onSubmit={handleSubmit}>
                {step === 1 && (
                  <>
                    <div className="cptx-field">
                      <label className="cptx-label">Full name</label>
                      <input
                        className="cptx-input"
                        style={{ marginTop: '6px' }}
                        placeholder="Jane Doe"
                        type="text"
                        autoComplete="fullName"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>

                    <div className="cptx-field">
                      <label className="cptx-label">Work email</label>
                      <input
                        ref={emailInputRef}
                        className="cptx-input"
                        style={{ marginTop: '6px' }}
                        placeholder="you@clinic.com"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={
                          isGoogleSignup
                            ? undefined
                            : (e) => setEmail(e.target.value)
                        }
                        disabled={isGoogleSignup}
                        title={
                          isGoogleSignup
                            ? `Confirmed by ${signupIdpName} - sign in again to use a different address`
                            : undefined
                        }
                      />
                    </div>

                    {/* Password fields. A Google signup has no password - the
                        gst token in the URL stands in for one. */}
                    {isGoogleSignup ? (
                      <div className="cptx-verify-target" style={{ fontWeight: 500 }}>
                        <i
                          className="ni ni-check-bold"
                          style={{ color: '#2dce89', marginRight: '6px' }}
                        />
                        Email confirmed by {signupIdpName}. You'll sign in with
                        the {signupIdpName} button from now on — no password
                        needed.
                      </div>
                    ) : (
                      <>
                        <div className="cptx-field">
                          <div className="cptx-field-row">
                            <label className="cptx-label">Password</label>
                            <button
                              type="button"
                              className="cptx-hint-link"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? 'Hide' : 'Show'}
                            </button>
                          </div>
                          <input
                            className="cptx-input"
                            placeholder="Create a password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            value={password}
                            onChange={handlePasswordChange}
                          />
                        </div>
                        <div className="cptx-chip-row">
                          {[
                            { ok: hasMinLength, label: '8+ characters' },
                            { ok: hasLowercase, label: 'lowercase letter' },
                            { ok: hasUppercase, label: 'uppercase letter' },
                            { ok: hasNumber, label: 'number' },
                          ].map((req) => (
                            <span
                              key={req.label}
                              className={`cptx-chip${req.ok ? ' cptx-chip-ok' : ''}`}
                            >
                              {req.ok && (
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M20 6 9 17l-5-5"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                              {req.label}
                            </span>
                          ))}
                        </div>

                        <div className="cptx-field">
                          <div className="cptx-field-row">
                            <label className="cptx-label">
                              Confirm password
                            </label>
                            <button
                              type="button"
                              className="cptx-hint-link"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            >
                              {showConfirmPassword ? 'Hide' : 'Show'}
                            </button>
                          </div>
                          <input
                            className="cptx-input"
                            placeholder="Re-enter password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                          />
                          {confirmPassword &&
                            password !== confirmPassword &&
                            !isTypingConfirmError && (
                              <div className="cptx-error-text">
                                Passwords do not match
                              </div>
                            )}
                        </div>
                      </>
                    )}

                    <div
                      className={`cptx-check-row${agreeTerms ? ' cptx-checked' : ''}`}
                      onClick={() => setAgreeTerms(!agreeTerms)}
                    >
                      <input
                        className="cptx-checkbox-native"
                        id="customCheckRegister"
                        type="checkbox"
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                      />
                      <div className="cptx-checkbox-box" />
                      <p>
                        I agree to the{' '}
                        <a
                          href={require('../assets/docs/Comprehend PT - Business Associate Agreement.pdf')}
                          target="blank"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Business Associate Agreement
                        </a>{' '}
                        and{' '}
                        <a
                          href={require('../assets/docs/Comprehend PT - Service Agreement.pdf')}
                          target="blank"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms of Service
                        </a>
                        . You can view it under Settings → Profile.
                      </p>
                    </div>
                    <div
                      className={`cptx-check-row${remember ? ' cptx-checked' : ''}`}
                      onClick={() => setRemember(!remember)}
                    >
                      <input
                        className="cptx-checkbox-native"
                        id="customCheckRegisterRemember"
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                      />
                      <div className="cptx-checkbox-box" />
                      <p>Remember me</p>
                    </div>

                    <button className="cptx-btn-primary" type="submit">
                      Continue
                    </button>
                  </>
                )}

                {step === 2 && (
                  <>
                    <div className="cptx-field">
                      <label className="cptx-label">Profession</label>
                      <div className="cptx-input-wrap" style={{ marginTop: '6px' }}>
                        <select
                          className="cptx-input"
                          value={profession}
                          onChange={(e) => setProfession(e.target.value)}
                        >
                          <option value="">Select profession</option>
                          {PROFESSION_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <svg
                          className="cptx-select-chevron"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M6 9l6 6 6-6"
                            stroke="#4d5d55"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>

                    <div className="cptx-field">
                      <div className="cptx-field-row">
                        <label className="cptx-label">Specialty</label>
                        <span className="cptx-hint-link" style={{ color: 'var(--cptx-ink-faint)', cursor: 'default' }}>
                          Optional
                        </span>
                      </div>
                      <div className="cptx-input-wrap">
                        <select
                          className="cptx-input"
                          value={speciality}
                          onChange={(e) => setSpeciality(e.target.value)}
                        >
                          <option value="">Speciality (optional)</option>
                          {SPECIALITY_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <svg
                          className="cptx-select-chevron"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M6 9l6 6 6-6"
                            stroke="#4d5d55"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>

                    <div className="cptx-field">
                      <label className="cptx-label">EMR system</label>
                      <div className="cptx-input-wrap" style={{ marginTop: '6px' }}>
                        <select
                          className="cptx-input"
                          value={emr}
                          onChange={(e) => setEmr(e.target.value)}
                        >
                          <option value="">EMR system</option>
                          {EMR_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <svg
                          className="cptx-select-chevron"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M6 9l6 6 6-6"
                            stroke="#4d5d55"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>

                    <div className="cptx-field">
                      <div className="cptx-field-row">
                        <label className="cptx-label">
                          How'd you hear about us?
                        </label>
                        {!promoLocked && (
                          <span
                            className="cptx-hint-link"
                            style={{ color: 'var(--cptx-ink-faint)', cursor: 'default' }}
                          >
                            Optional
                          </span>
                        )}
                      </div>
                      <input
                        className="cptx-input"
                        placeholder="A colleague, a talk…"
                        type="text"
                        value={promoCode}
                        onChange={
                          promoLocked
                            ? undefined
                            : (e) => setPromoCode(e.target.value)
                        }
                        disabled={promoLocked}
                        title={
                          promoLocked
                            ? 'Promo code locked from referral link'
                            : undefined
                        }
                      />
                    </div>

                    <button
                      className="cptx-btn-primary"
                      type="submit"
                      disabled={!agreeTerms || disableCreateAccount}
                    >
                      Create account
                    </button>
                  </>
                )}
              </Form>
            </div>
          </div>
        </div>
      </Col>
    </>
  );
};

export default Register;
